import { useEffect, useRef, useState } from 'react'
import type { SelectedFile } from '../types'

export interface FileContentState {
  content: string | null
  loading: boolean
  error: string | null
}

// Resolves the content for a SelectedFile. If the file already carries an
// inline content (e.g. bootstrap files), returns it immediately. Otherwise
// fetches from `fileContentUrl?path=<encoded>` and caches by path so re-clicks
// are instant. `preloadEntries` lets the caller seed the cache (used for the
// API-supplied default file).
export function useFileContent(
  file: SelectedFile | null,
  fileContentUrl: string | null | undefined,
  preloadEntries?: Array<{ path: string; content: string }>,
): FileContentState {
  const cacheRef = useRef<Map<string, string>>(new Map())
  const [state, setState] = useState<FileContentState>({ content: null, loading: false, error: null })

  if (preloadEntries) {
    for (const entry of preloadEntries) {
      if (!cacheRef.current.has(entry.path)) cacheRef.current.set(entry.path, entry.content)
    }
  }

  useEffect(() => {
    if (!file) {
      setState({ content: null, loading: false, error: null })
      return
    }

    if (file.content != null) {
      cacheRef.current.set(file.path, file.content)
      setState({ content: file.content, loading: false, error: null })
      return
    }

    const cached = cacheRef.current.get(file.path)
    if (cached != null) {
      setState({ content: cached, loading: false, error: null })
      return
    }

    if (!fileContentUrl) {
      setState({ content: null, loading: false, error: null })
      return
    }

    const controller = new AbortController()
    setState({ content: null, loading: true, error: null })

    const url = `${fileContentUrl}?path=${encodeURIComponent(file.path)}`
    fetch(url, { headers: { Accept: 'application/json' }, signal: controller.signal })
      .then(async (res) => {
        const body = await res.json().catch(() => ({}))
        if (!res.ok || body.ok === false) {
          throw new Error(body.error || `HTTP ${res.status}`)
        }
        const content = typeof body.content === 'string' ? body.content : ''
        cacheRef.current.set(file.path, content)
        setState({ content, loading: false, error: null })
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return
        const message = err instanceof Error ? err.message : 'erro ao carregar arquivo'
        setState({ content: null, loading: false, error: message })
      })

    return () => controller.abort()
  }, [file?.path, file?.content, fileContentUrl])

  return state
}
